import { baseQueryWithoutToken } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { TransactionRequest } from "shared/models/transaction.model";

export const authService = createApi({
  reducerPath: "authService",
  baseQuery: baseQueryWithoutToken,
  endpoints: (builder) => ({
    login: builder.mutation<any, any>({
      query: (body: any) => ({
        url: "/auth/login",
        body,
        method: "POST",
      }),
    }),
    sendEmailForgotPassword: builder.mutation<any, { email: string }>({
      query: (params) => ({
        url: "/auth/forgot-password",
        params,
        method: "POST",
      }),
    }),
    resetPassword: builder.mutation<any, { ftoken: string; password: string }>({
      query: (body) => ({
        url: "/auth/reset-password",
        body,
        method: "POST",
      }),
    }),
    checkTokenForgotExpired: builder.mutation<any, { ftoken: string }>({
      query: (params) => ({
        url: `/auth/forgot-password/${params?.ftoken}`,
        method: "POST",
      }),
    }),
    getIframeTransaction: builder.query<any, { public_key: string; data: TransactionRequest }>({
      query: ({ public_key, data }) => ({
        url: `/iframe/transactions`,
        method: "GET",
        params: { ...data, public_key },
      }),
    }),
    getIframeDetailTransaction: builder.query<any, { public_key: string; id: string }>({
      query: ({ public_key, id }) => ({
        url: `/iframe/transactions/${id}`,
        method: "GET",
        params: { public_key },
      }),
    }),

    getIframeDisbursement: builder.query<any, any>({
      query: ({ public_key, ...data }) => ({
        url: `/iframe/disbursements`,
        method: "GET",
        params: { ...data, public_key },
      }),
    }),

    getIframeMerchants: builder.query<
      any,
      {
        public_key: string;
        page?: number;
        limit?: number;
        search?: string;
        skip_log?: boolean;
        partnerId?: number;
      }
    >({
      query: (params) => ({
        url: `/iframe/merchants`,
        method: "GET",
        params: params,
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success === true) {
          const data = rawData?.data.map((raw: any) => ({
            value: raw.id,
            name: raw.trading_name,
          }));
          const sort = data.sort((a: any, b: any) => a?.name.localeCompare(b?.name));
          return { data: sort, pagination: rawData.meta };
        }
      },
    }),
    getIframePartners: builder.query<
      any,
      {
        public_key: string;
        page?: number;
        limit?: number;
        search?: string;
        skip_log?: boolean;
      }
    >({
      query: (params) => ({
        url: `/iframe/partners`,
        method: "GET",
        params: params,
      }),
      transformResponse: (rawData: any) => {
        if (rawData?.success) {
          const data = rawData?.data
            .map((raw: any) => ({
              value: raw.id,
              name: raw.business_name,
            }))
            .sort((a: any, b: any) => a?.name.localeCompare(b?.name));
          return { data, pagination: rawData.meta };
        }
      },
    }),
    checkPublicKey: builder.mutation<
      any,
      {
        public_key: string;
      }
    >({
      query: ({ public_key }) => ({
        url: `/check-public-key`,
        body: { public_key },
        method: "POST",
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useSendEmailForgotPasswordMutation,
  useResetPasswordMutation,
  useCheckTokenForgotExpiredMutation,
  useLazyGetIframeTransactionQuery,
  useCheckPublicKeyMutation,
  useLazyGetIframeMerchantsQuery,
  useLazyGetIframePartnersQuery,
  useLazyGetIframeDetailTransactionQuery,
  useGetIframeDisbursementQuery,
} = authService;
